import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useCallback, useEffect, useRef, useState} from 'react'

import type {CodeLineData} from '../components/blob/BlobContent/Code/hooks/use-code-lines'

export const DELETE_STICKY_LINES_VALUE = null

export type SetStickyLinesType = (codeLineData: CodeLineData | null, removeStickyLine: boolean) => void

interface StickyLineOp {
  codeLineData: CodeLineData | null
  removeStickyLine: boolean
}

// Pure reducer for a single stick/unstick operation. Returns `prev` unchanged for no-ops so
// React (and the coalesced reduce below) can bail out of redundant re-renders.
function applyStickyLineOp(
  prev: Map<number, CodeLineData>,
  codeLineData: CodeLineData | null,
  removeStickyLine: boolean,
): Map<number, CodeLineData> {
  // No-op delete when already empty
  if (codeLineData === null && prev.size === 0) {
    return prev
  }

  // Clear all sticky lines
  if (codeLineData === null && removeStickyLine) {
    if (prev.size === 0) {
      return prev
    }
    return new Map()
  }

  if (!codeLineData) {
    return prev
  }

  const lineNumber = codeLineData.lineNumber
  const next = new Map(prev)

  if (removeStickyLine) {
    next.delete(lineNumber)
  } else {
    next.set(lineNumber, codeLineData)
  }

  const activeLine = next.get(lineNumber)
  if (activeLine) {
    for (const [otherLineNumber, otherStickyLine] of next) {
      // Skip the line we just added/updated
      if (otherLineNumber === lineNumber) {
        continue
      }

      // Delete lines that are outside valid scope:
      // - No ownedSection, OR
      // - Section ends before current line, OR
      // - Current line is before the other line
      if (
        !otherStickyLine.ownedSection ||
        otherStickyLine.ownedSection.endLine < lineNumber ||
        lineNumber < otherStickyLine.lineNumber
      ) {
        next.delete(otherLineNumber)
      }
    }
  }

  return next
}

export function useStickyLines() {
  const [currentStickyLines, setCurrentStickyLines] = useState<Map<number, CodeLineData>>(() => new Map())

  // During fast scroll, IntersectionObserver callbacks fire many stick/unstick updates across a
  // single frame, each cloning the Map and re-rendering the sticky-line overlay. Behind
  // `code_view_raf_sticky_lines` we queue the operations and apply them in a single
  // requestAnimationFrame, collapsing a burst into one state update / re-render per frame.
  const pendingOpsRef = useRef<StickyLineOp[]>([])
  const rafIdRef = useRef(0)

  useEffect(() => {
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
    }
  }, [])

  const setStickyLines = useCallback<SetStickyLinesType>((codeLineData, removeStickyLine) => {
    if (!isFeatureEnabled('code_view_raf_sticky_lines')) {
      setCurrentStickyLines(prev => applyStickyLineOp(prev, codeLineData, removeStickyLine))
      return
    }

    pendingOpsRef.current.push({codeLineData, removeStickyLine})
    if (rafIdRef.current) return
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = 0
      const ops = pendingOpsRef.current
      pendingOpsRef.current = []
      setCurrentStickyLines(prev =>
        ops.reduce((acc, op) => applyStickyLineOp(acc, op.codeLineData, op.removeStickyLine), prev),
      )
    })
  }, [])

  return {currentStickyLines, setStickyLines}
}
